/**
 * ChannelAlertsContext
 *
 * Recebe alertas Cloud API (e futuros canais) via window.postMessage emitido
 * pelo NotificationsContext quando WS do backend envia evento iniciado por
 * "cloud_api_*" (ou outros prefixes de canais futuros).
 *
 * Mantém um feed in-memory por sessão. Persiste em sessionStorage para
 * sobreviver F5. NÃO persiste no backend — backend já tem sua própria
 * audit trail; este feed é UX.
 *
 * Eventos esperados (do backend Fase 2):
 *   - cloud_api_token_invalid       (severity: error)
 *   - cloud_api_token_expiring      (severity: warning|error)
 *   - cloud_api_phone_quality       (severity: info|warning|error)
 *   - cloud_api_template_status     (severity: info|warning)
 *   - cloud_api_template_category_changed (severity: warning)
 *   - cloud_api_meta_alert          (severity: info|warning|error)
 *   - cloud_api_webhook_silent      (severity: warning)
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

const STORAGE_KEY = 'channel_alerts_feed_v1';
const MAX_STORED = 100;

// Eventos que tratamos como alertas de canal (não-mensagem).
const KNOWN_CHANNEL_EVENT_PREFIXES = [
  'cloud_api_',
  // futuro: 'instagram_', 'messenger_', 'site_', etc.
];

const ChannelAlertsContext = createContext(null);

function loadFromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveToStorage(alerts) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(alerts.slice(0, MAX_STORED)));
  } catch {
    // sessionStorage cheio ou bloqueado — silencioso
  }
}

function generateId() {
  return `alert_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

const initialState = {
  alerts: loadFromStorage(),
};

function reducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const alerts = [action.alert, ...state.alerts].slice(0, MAX_STORED);
      saveToStorage(alerts);
      return { ...state, alerts };
    }
    case 'MARK_READ': {
      const alerts = state.alerts.map((a) =>
        a.id === action.id ? { ...a, read: true } : a,
      );
      saveToStorage(alerts);
      return { ...state, alerts };
    }
    case 'MARK_ALL_READ': {
      const alerts = state.alerts.map((a) => ({ ...a, read: true }));
      saveToStorage(alerts);
      return { ...state, alerts };
    }
    case 'DISMISS': {
      const alerts = state.alerts.filter((a) => a.id !== action.id);
      saveToStorage(alerts);
      return { ...state, alerts };
    }
    case 'CLEAR': {
      saveToStorage([]);
      return { ...state, alerts: [] };
    }
    default:
      return state;
  }
}

export function ChannelAlertsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Ref para deduplicação — handler precisa ler ALERTS ATUALIZADOS, mas
  // useEffect com deps=[] captura state inicial em closure (stale).
  // Sincronizamos ref a cada render.
  const alertsRef = useRef(state.alerts);
  useEffect(() => {
    alertsRef.current = state.alerts;
  });

  // Escuta postMessage emitido pelo NotificationsContext.
  useEffect(() => {
    const handler = (event) => {
      const data = event?.data;
      if (!data || data.type !== 'channel_alert') return;

      const evt = data.event || '';
      const isKnown = KNOWN_CHANNEL_EVENT_PREFIXES.some((p) => evt.startsWith(p));
      if (!isKnown) return;

      // Idempotência via ref (sempre lê alerts mais recentes).
      const dedupeKey = `${evt}_${data.cloud_account_id || ''}_${data.timestamp || ''}`;
      const isDuplicate = alertsRef.current.some(
        (a) => a._dedupeKey === dedupeKey,
      );
      if (isDuplicate) return;

      dispatch({
        type: 'ADD',
        alert: {
          id: generateId(),
          _dedupeKey: dedupeKey,
          event: evt,
          severity: data.severity || 'info',
          message: data.message || '',
          channelKey: evt.split('_').slice(0, 2).join('_'), // 'cloud_api'
          cloudAccountId: data.cloud_account_id,
          phone: data.phone,
          extra: data,
          read: false,
          createdAt: Date.now(),
        },
      });
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const addAlert = useCallback((alert) => {
    dispatch({ type: 'ADD', alert: { id: generateId(), ...alert, read: false, createdAt: Date.now() } });
  }, []);
  const markRead = useCallback((id) => dispatch({ type: 'MARK_READ', id }), []);
  const markAllRead = useCallback(() => dispatch({ type: 'MARK_ALL_READ' }), []);
  const dismiss = useCallback((id) => dispatch({ type: 'DISMISS', id }), []);
  const clear = useCallback(() => dispatch({ type: 'CLEAR' }), []);

  const unreadCount = useMemo(
    () => state.alerts.filter((a) => !a.read).length,
    [state.alerts],
  );

  const criticalCount = useMemo(
    () => state.alerts.filter((a) => !a.read && a.severity === 'error').length,
    [state.alerts],
  );

  const value = useMemo(
    () => ({
      alerts: state.alerts,
      unreadCount,
      criticalCount,
      addAlert,
      markRead,
      markAllRead,
      dismiss,
      clear,
    }),
    [state.alerts, unreadCount, criticalCount, addAlert, markRead, markAllRead, dismiss, clear],
  );

  return (
    <ChannelAlertsContext.Provider value={value}>
      {children}
    </ChannelAlertsContext.Provider>
  );
}

export function useChannelAlerts(filters = {}) {
  const ctx = useContext(ChannelAlertsContext);
  if (!ctx) {
    throw new Error('useChannelAlerts deve ser usado dentro de ChannelAlertsProvider');
  }

  // Filtros opcionais
  const filtered = useMemo(() => {
    let out = ctx.alerts;
    if (filters.channelKey) {
      out = out.filter((a) => a.channelKey === filters.channelKey);
    }
    if (filters.cloudAccountId) {
      out = out.filter((a) => a.cloudAccountId === filters.cloudAccountId);
    }
    if (filters.severity) {
      out = out.filter((a) => a.severity === filters.severity);
    }
    if (filters.unreadOnly) {
      out = out.filter((a) => !a.read);
    }
    if (filters.event) {
      out = out.filter((a) => a.event === filters.event);
    }
    return out;
  }, [ctx.alerts, filters.channelKey, filters.cloudAccountId, filters.severity, filters.unreadOnly, filters.event]);

  return {
    ...ctx,
    alerts: filtered,
  };
}

export default ChannelAlertsContext;
